import React, { useState, useEffect, useRef } from 'react';
import {
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Shield,
  Bell,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import './DashboardLayout.css';

/**
 * DashboardLayout
 * 
 * A mobile-first responsive layout shell for Super Admin, Admin, Teacher, and Student roles.
 * Supports:
 * - Mobile fixed topbar with logo, hamburger trigger, and profile pill dropdown (< 1024px)
 * - Off-canvas slide-over drawer menu with backdrop and ESC key listener
 * - Desktop static sidebar (>= 1024px) with role badge and quick navigation
 * - Compact mobile header banner vs expanded gradient desktop header
 * - Full ARIA accessibility attributes and smooth transitions
 */
export default function DashboardLayout({
  brandName = 'Epsilon Skating Club',
  logo = null,
  userProfile = {},
  roleBadge = 'Admin',
  navLinks = [],
  activeTab = '',
  onTabChange = () => {},
  onLogout = () => {},
  headerTitle = 'Dashboard',
  headerSubtitle = '',
  headerActions = null,
  summaryStrip = null,
  children
}) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const userName = userProfile?.name || 
    (userProfile?.FirstName ? `${userProfile.FirstName} ${userProfile.LastName || ''}`.trim() : 'User');
  const userEmail = userProfile?.email || userProfile?.Email || 'user@epsilon.club';
  const avatarUrl = userProfile?.avatar || userProfile?.PhotoPath || null;

  // Auto-close drawer on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDrawerOpen]);

  // Handle ESC key to close drawer & profile dropdown
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsDrawerOpen(false);
        setIsProfileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  // Handle click outside profile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (link) => {
    onTabChange(link.id || link.tabKey || link.path);
    setIsDrawerOpen(false);
  };

  return (
    <div className="dash-shell">
      {/* 1. MOBILE TOP NAVIGATION BAR (< 1024px) */}
      <header className="dash-mobile-topbar" aria-label="Mobile Navigation Bar">
        <div className="dash-mobile-topbar-left">
          <button
            type="button"
            className="dash-hamburger-btn"
            onClick={() => setIsDrawerOpen(true)}
            aria-expanded={isDrawerOpen}
            aria-controls="dash-mobile-drawer"
            aria-label="Open Navigation Drawer"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="dash-brand-block">
            {logo ? (
              <img src={logo} alt={brandName} className="dash-brand-logo" />
            ) : (
              <div className="dash-brand-badge-icon">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
            )}
            <span className="dash-brand-title">{brandName}</span>
          </div>
        </div>

        <div className="dash-mobile-topbar-right" ref={profileMenuRef}>
          <button
            type="button"
            className={`dash-avatar-pill ${isProfileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
            aria-expanded={isProfileMenuOpen}
            aria-label="User profile menu"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt={userName} className="dash-avatar-img" />
            ) : (
              <div className="dash-avatar-placeholder">
                {userName.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="dash-avatar-name hidden sm:inline">{userName.split(' ')[0]}</span>
            <ChevronDown className={`dash-pill-chevron ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Profile Dropdown Menu */}
          {isProfileMenuOpen && (
            <div className="dash-profile-dropdown" role="menu">
              <div className="dash-dropdown-header">
                <p className="dash-dropdown-name">{userName}</p>
                <p className="dash-dropdown-email">{userEmail}</p>
                <span className="dash-role-tag">{roleBadge}</span>
              </div>
              <div className="dash-dropdown-divider" />
              <button
                type="button"
                className="dash-dropdown-item"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  onTabChange('profile');
                }}
                role="menuitem"
              >
                <User className="w-4 h-4 mr-2" />
                View Profile
              </button>
              <button
                type="button"
                className="dash-dropdown-item text-red-600"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  onLogout();
                }}
                role="menuitem"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* 2. SLIDE-OVER MOBILE DRAWER & BACKDROP */}
      <div
        className={`dash-drawer-backdrop ${isDrawerOpen ? 'open' : ''} bg-black/50 backdrop-blur-sm`}
        onClick={() => setIsDrawerOpen(false)}
        aria-hidden="true"
      />

      <aside
        id="dash-mobile-drawer"
        className={`dash-mobile-drawer ${isDrawerOpen ? 'open' : ''} transition-transform duration-300`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation Menu"
      >
        <div className="dash-drawer-header">
          <div className="dash-brand-block">
            {logo ? (
              <img src={logo} alt={brandName} className="dash-brand-logo" />
            ) : (
              <div className="dash-brand-badge-icon">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
            )}
            <div>
              <span className="dash-brand-title">{brandName}</span>
              <span className="dash-brand-sub">Management Portal</span>
            </div>
          </div>
          <button
            type="button"
            className="dash-drawer-close-btn"
            onClick={() => setIsDrawerOpen(false)}
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Drawer User Banner */}
        <div className="dash-drawer-user-card">
          <div className="dash-avatar-large">
            {avatarUrl ? (
              <img src={avatarUrl} alt={userName} />
            ) : (
              <span>{userName.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="dash-drawer-user-info">
            <h4 className="dash-drawer-user-name">{userName}</h4>
            <span className="dash-role-badge">{roleBadge}</span>
          </div>
        </div>

        {/* Drawer Nav Links */}
        <nav className="dash-drawer-nav">
          <p className="dash-nav-group-title">MAIN NAVIGATION</p>
          {navLinks.map((link) => {
            const linkId = link.id || link.tabKey || link.path;
            const isActive = activeTab === linkId;
            const IconComponent = link.icon;

            return (
              <button
                key={linkId}
                type="button"
                className={`dash-drawer-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNavClick(link)}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="dash-nav-icon">
                  {typeof IconComponent === 'function' || typeof IconComponent === 'object' ? (
                    React.isValidElement(IconComponent) ? IconComponent : <IconComponent className="w-5 h-5" />
                  ) : (
                    <Shield className="w-5 h-5" />
                  )}
                </span>
                <span className="dash-nav-label">{link.label}</span>
                {link.badge !== undefined && link.badge !== null && (
                  <span className={`dash-nav-badge ${isActive ? 'active' : ''}`}>
                    {link.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="dash-drawer-footer">
          <button
            type="button"
            className="dash-drawer-logout-btn"
            onClick={() => {
              setIsDrawerOpen(false);
              onLogout();
            }}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* 3. DESKTOP SIDEBAR (>= 1024px) */}
      <aside className="dash-desktop-sidebar">
        <div className="dash-sidebar-header">
          <div className="dash-brand-block">
            {logo ? (
              <img src={logo} alt={brandName} className="dash-brand-logo" />
            ) : (
              <div className="dash-brand-badge-icon">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <span className="dash-brand-title">{brandName}</span>
              <span className="dash-brand-sub">Club Management</span>
            </div>
          </div>
        </div>

        <nav className="dash-sidebar-nav">
          <div className="dash-nav-group">
            <p className="dash-nav-group-title">NAVIGATION</p>
            {navLinks.map((link) => {
              const linkId = link.id || link.tabKey || link.path;
              const isActive = activeTab === linkId;
              const IconComponent = link.icon;

              return (
                <button
                  key={linkId}
                  type="button"
                  className={`dash-sidebar-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => onTabChange(linkId)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="dash-nav-icon">
                    {React.isValidElement(IconComponent) ? (
                      IconComponent
                    ) : typeof IconComponent === 'function' ? (
                      <IconComponent className="w-5 h-5" />
                    ) : (
                      <Shield className="w-5 h-5" />
                    )}
                  </span>
                  <span className="dash-nav-label">{link.label}</span>
                  {link.badge !== undefined && link.badge !== null && (
                    <span className={`dash-nav-badge ${isActive ? 'active' : ''}`}>
                      {link.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Desktop Sidebar Footer */}
        <div className="dash-sidebar-user-footer">
          <div className="dash-sidebar-user-info">
            <div className="dash-avatar-sm">
              {avatarUrl ? (
                <img src={avatarUrl} alt={userName} />
              ) : (
                <span>{userName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="dash-user-text">
              <p className="dash-user-name">{userName}</p>
              <span className="dash-role-pill">{roleBadge}</span>
            </div>
          </div>
          <button
            type="button"
            className="dash-icon-logout-btn"
            onClick={onLogout}
            title="Sign Out"
            aria-label="Sign Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* 4. MAIN CONTENT CONTAINER & HEADER BANNERS */}
      <div className="dash-main-container">
        {/* Responsive Header Banner */}
        <header className="dash-hero-banner">
          <div className="dash-hero-content">
            <div className="dash-hero-title-area">
              <span className="dash-kicker-tag">{brandName.toUpperCase()}</span>
              <h1 className="dash-hero-heading">{headerTitle}</h1>
              {headerSubtitle && <p className="dash-hero-subheading">{headerSubtitle}</p>}
            </div>

            {/* Desktop User Info & Actions (Consolidated compact on mobile) */}
            <div className="dash-hero-right-panel">
              <div className="dash-hero-user-badge hidden lg:flex">
                <div className="dash-avatar-md">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={userName} />
                  ) : (
                    <span>{userName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <div className="dash-hero-user-name-wrap">
                    <p className="dash-hero-user-name">{userName}</p>
                    <span className="dash-hero-role">{roleBadge}</span>
                  </div>
                  <p className="dash-hero-user-email">{userEmail}</p>
                </div>
              </div>

              {headerActions && <div className="dash-hero-actions">{headerActions}</div>}
            </div>
          </div>
        </header>

        {/* Optional Metric Summary Strip */}
        {summaryStrip && <div className="dash-summary-container">{summaryStrip}</div>}

        {/* Main Children Outlet */}
        <main className="dash-content-outlet">
          {children}
        </main>
      </div>
    </div>
  );
}
